import { Link } from 'react-router-dom';
import { FiTruck, FiInbox, FiClock, FiChevronRight } from 'react-icons/fi';
import { formatarDataBR } from '../../lib/data';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { useAuth } from '../../contexts/AuthContext';
import { useDadosVivos } from '../../hooks/useDadosVivos';
import { fornecedorService, STATUS_OS_LABEL, type OrdemFornecedor } from '../../services/fornecedor.service';

const STATUS_COR: Record<OrdemFornecedor['status'], string> = {
  enviada: 'bg-blue-50 text-blue-700 border-blue-200',
  em_execucao: 'bg-amber-50 text-amber-700 border-amber-200',
  concluida: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelada: 'bg-slate-100 text-slate-600 border-slate-200',
};

export function FornecedorOrdensPage() {
  const { cidadao } = useAuth();
  const { dados, carregando, erro } = useDadosVivos<OrdemFornecedor[]>(
    () => fornecedorService.minhasOrdens(),
    [],
  );
  const ordens = dados ?? [];

  return (
    <PortalPage
      titulo="Ordens de serviço"
      subtitulo="Ordens emitidas pela prefeitura para a sua empresa"
      icone={<FiTruck size={26} />}
      largura="media"
    >
      {!cidadao?.cnpj && (
        <Alert variante="aviso">
          Esta área é destinada a <strong>empresas/fornecedores</strong>. Cadastre-se (ou complete seu cadastro) com o
          <strong> CNPJ</strong> para visualizar as ordens de serviço emitidas para a sua empresa.
        </Alert>
      )}

      {carregando ? (
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      ) : erro ? (
        <Alert variante="erro">{erro}</Alert>
      ) : ordens.length === 0 ? (
        cidadao?.cnpj && (
          <SectionCard>
            <div className="flex flex-col items-center py-10 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400"><FiInbox size={26} /></div>
              <p className="text-sm font-medium text-slate-700">Nenhuma ordem de serviço para a sua empresa ainda.</p>
              <p className="mt-1 text-xs text-slate-500">Quando a prefeitura emitir uma OS para o seu CNPJ, ela aparecerá aqui.</p>
            </div>
          </SectionCard>
        )
      ) : (
        <div className="space-y-3">
          {ordens.map((o) => (
            <Link key={o.id} to={`/fornecedor/${o.numero ?? o.id}`} className="block">
              <SectionCard className="transition hover:border-institutional/40 hover:shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    {o.numero && <div className="font-mono text-xs text-institutional">{o.numero}</div>}
                    <h3 className="font-semibold text-slate-900">{o.titulo}</h3>
                    {o.descricao && <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-slate-600">{o.descricao}</p>}
                    <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1"><FiClock size={11} /> {formatarDataBR(o.criado_em)}</span>
                      {o.prefeitura && <span>· {o.prefeitura}</span>}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_COR[o.status]}`}>
                      {STATUS_OS_LABEL[o.status]}
                    </span>
                    <FiChevronRight className="text-slate-300" size={18} />
                  </div>
                </div>
              </SectionCard>
            </Link>
          ))}
        </div>
      )}
    </PortalPage>
  );
}
